import clsx from "clsx";

/**
 * Esqueleto de carga — sección 8.2.
 *
 * LA SILUETA IMPORTA MÁS QUE EL BRILLO
 *   Cada variante reserva **la misma altura** que el contenido que va a sustituirla, de modo que
 *   la página no dé un salto cuando llegan los datos. Por eso `pitch` no es un rectángulo
 *   cualquiera: guarda la proporción exacta del campo.
 *
 * MOVIMIENTO
 *   El pulso es una animación de opacidad. Con `prefers-reduced-motion` la hoja de estilos la
 *   apaga entera y el esqueleto se queda quieto, que sigue diciendo lo mismo.
 */

export type SkeletonVariant = "text" | "card" | "row" | "chart" | "pitch";

const VARIANTS: Record<SkeletonVariant, string> = {
  text: "h-3 rounded-md",
  row: "h-[60px] rounded-xl",
  card: "h-[92px] rounded-xl",
  chart: "h-[150px] rounded-xl",
  // La misma proporción que `.pitch`, para que el campo no empuje la página al aparecer.
  pitch: "aspect-[72/100] rounded-xl",
};

export interface SkeletonProps {
  variant?: SkeletonVariant;
  /** Cuántas siluetas seguidas. Útil para listas: `<Skeleton variant="row" count={5} />`. */
  count?: number;
  className?: string;
  /** Qué está cargando, para quien no ve la pantalla. */
  label?: string;
}

export function Skeleton({
  variant = "text",
  count = 1,
  className,
  label = "Cargando",
}: SkeletonProps) {
  const piezas = Math.max(1, Math.trunc(count));

  return (
    // `aria-busy` en el grupo y `aria-hidden` en cada pieza: se anuncia una vez, no una por silueta.
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className={clsx("grid gap-2", className)}
      data-variant={variant}
    >
      {Array.from({ length: piezas }, (_, indice) => (
        <div
          key={indice}
          aria-hidden="true"
          data-testid="skeleton-piece"
          className={clsx("animate-pulse bg-white/[0.07]", VARIANTS[variant])}
        />
      ))}
    </div>
  );
}
